import React, { useEffect, useState } from "react";
import {
  StyledVideoTitle,
  StyledContent,
  StyledTags,
  StyledSubContainer,
  StyledVideoLength,
  StyledVideoLengthContainer,
} from "./styled";
import { ThumbnailProps } from "../../types/ThumbnailProps";
import {
  isTextNeedsEllipsis,
  toHHMMSS,
  uniqueArray,
} from "../../utils/utilFunctions";
import { getThumbnailByVideoID } from "../../services/contentServices";
import {
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  CardMedia,
  IconButton,
  Skeleton,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { diamond, antiFlashWhite } from "../../utils/StyleHelpers";
import ThumbnailMenu from "../ThumbnailMenu";
import { useContentStore } from "../../stores/contentStore";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";
import { locale } from "../../utils/LocaleManager";
import { TagTypeEnum } from "../../enums/TagTypeEnum";

const Thumbnail = (props: ThumbnailProps) => {
  const selectedThumbnails = useContentStore(
    (state: any) => state.selectedThumbnails,
  );
  const setSelectedThumbnails = useContentStore(
    (state: any) => state.setSelectedThumbnails,
  );
  const isShowPlayer = useContentStore((state: any) => state.isShowPlayer);
  const [imageData, setImageData] = useState("");
  const [selected, setSelected] = useState(false);
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [contextMenu, setContextMenu] = React.useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [tags, setTags] = useState<any>();
  const searchKeyword = useContentStore((state: any) => state.searchKeyword);
  const isLoading = useContentStore((state: any) => state.isLoading);

  const rolePermissions = useRolePermissionsStore(
    (state: any) => state.rolePermissions,
  );
  const isUserVideoRole =
    rolePermissions?.video?.manage === false &&
    rolePermissions?.video?.retag === false &&
    rolePermissions?.video?.delete === false &&
    rolePermissions?.video?.edit === false &&
    rolePermissions?.video?.rename === false &&
    rolePermissions?.video?.download === false &&
    rolePermissions?.video?.move === false &&
    rolePermissions?.video?.view === true &&
    rolePermissions?.video?.browse === true;
  const currentThumbnail = {
    videoIndexId: props.videoIndexId,
    image: imageData,
    name: props.name,
  };

  const selectThumbnail = () => {
    setSelected((prevSelected) => {
      let selectedList = selectedThumbnails.slice(0);
      if (!prevSelected) {
        selectedList = isUserVideoRole
          ? [currentThumbnail]
          : [...selectedList, currentThumbnail];
        setSelectedThumbnails(selectedList);
        return !prevSelected;
      }

      selectedList = selectedList.filter(
        (item: any) => item.videoIndexId !== props.videoIndexId,
      );
      setSelectedThumbnails(selectedList);

      return !prevSelected;
    });
  };

  const pushSelectedThumbnail = () => {
    if (
      selectedThumbnails.find(
        (thumbnail: any) => thumbnail.videoIndexId === props.videoIndexId,
      )
    )
      return;
    setSelectedThumbnails(
      isUserVideoRole
        ? [currentThumbnail]
        : [...selectedThumbnails, currentThumbnail],
    );
  };

  const onMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setSelected(true);
    setAnchorEl(e.currentTarget);
    setContextMenu(null);
    setOpen(true);
    pushSelectedThumbnail();
  };

  const onContextMenuOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setSelected(true);
    setAnchorEl(null);
    setContextMenu({
      mouseX: e.clientX + 2,
      mouseY: e.clientY - 6,
    });
    setOpen(true);
    pushSelectedThumbnail();
  };

  const sortTags = (a: any, b: any) => {
    if (a.confidence > b.confidence) {
      return -1;
    }
    if (a.confidence < b.confidence) {
      return 1;
    }
    return 0;
  };

  const tagLists = (props: any) => {
    const tags = props.tags.sort(sortTags).map((tag: any) => tag.name);
    setTags(tags.filter(uniqueArray).join(","));
  };

  const transcriptTags = (props: any) => {
    const result = props.tags
      .filter((tagType: any) => tagType.type !== TagTypeEnum.transcript)
      .sort(sortTags)
      .map((tagName: any) => tagName.name);
    setTags(result.filter(uniqueArray).join(","));
  };

  useEffect(() => {
    let isActive = true;
    let thumbnailUrl = "";

    const loadThumbnail = async () => {
      if (!props.videoIndexId) return;

      const thumbnail = await getThumbnailByVideoID(props.videoIndexId);
      if (isActive && thumbnail instanceof Blob) {
        thumbnailUrl = URL.createObjectURL(thumbnail);
        setImageData(thumbnailUrl);
      }
    };

    void loadThumbnail();

    if (props.tags && props.tags.length) {
      if (!searchKeyword) {
        transcriptTags(props);
      } else {
        tagLists(props);
      }
    }

    return () => {
      isActive = false;
      if (thumbnailUrl) URL.revokeObjectURL(thumbnailUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  useEffect(() => {
    if (!selectedThumbnails.length) {
      setSelected(false);
      return;
    }

    setSelected(
      selectedThumbnails.find(
        (item: any) => item?.videoIndexId === props?.videoIndexId,
      ),
    );
  }, [selectedThumbnails, props?.videoIndexId]);

  useEffect(() => {
    if (isShowPlayer) {
      setOpen(false);
    }
  }, [isShowPlayer, open]);

  return isLoading && !props.isBrowseOnly ? (
    <Skeleton variant="rectangular" width="100%" height="13.125rem" />
  ) : (
    <Tooltip
      title={isTextNeedsEllipsis(props.name) ? props.name : ""}
      placement="top-end"
      arrow
    >
      <Card
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          flexShrink: 0,
          padding: "1rem",
          background: selected ? diamond : antiFlashWhite,
          boxShadow: "unset",
        }}
        onContextMenu={onContextMenuOpen}
      >
        <CardHeader
          avatar={<></>}
          action={
            <>
              <IconButton aria-label={`Open actions for video ${props.name}`} onClick={onMenuOpen}>
                <MoreVertIcon />
              </IconButton>
              <ThumbnailMenu
                open={open}
                name={props.name}
                setOpen={setOpen}
                anchorEl={anchorEl}
                setSelected={setSelected}
                videoIndexId={props.videoIndexId}
                onClickPlay={() => props.onClickPlay()}
                onDownload={() => props.onDownload()}
                contextMenu={contextMenu}
                onClickDetails={() => props.onClickDetails()}
                onCopyLinks={props.onCopyLinks}
                onClickEdit={props.onClickEdit}
                onClickMove={props.onClickMove}
                onClickRetag={props.onClickRetag}
                onUnassignVertical={props.onUnassignVertical}
                isBrowseOnly={
                  props.isBrowseOnly &&
                  !props.onClickEdit &&
                  !props.onClickMove &&
                  !props.onClickRetag
                }
                isMultiple={selectedThumbnails.length > 1}
              />
            </>
          }
          title={
            <CardActionArea
              onClick={selectThumbnail}
              onContextMenu={onContextMenuOpen}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: "1 0 0",
                textWrap: "nowrap",
                width: "16rem",
              }}
            >
              <StyledVideoTitle>{props.name}</StyledVideoTitle>
            </CardActionArea>
          }
          style={{
            padding: 0,
          }}
        />
        <CardActionArea
          onClick={selectThumbnail}
          onContextMenu={onContextMenuOpen}
        >
          <CardMedia
            component="img"
            src={imageData}
            style={{
              height: "13.125rem",
              borderRadius: "0.5rem",
              background: "lightgray 50% / cover no-repeat",
              marginBottom: "0.75rem",
            }}
            draggable="false"
          />
          <CardContent
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              flexShrink: 0,
              margin: 0,
              padding: 0,
            }}
          >
            <StyledContent>{locale.thumbnail_tags}</StyledContent>
            <Tooltip title={tags} placement="top-end" arrow>
              <StyledTags>{tags}</StyledTags>
            </Tooltip>
            <StyledSubContainer>
              <StyledVideoLengthContainer>
                <StyledContent>{locale.thumbnail_videolength}</StyledContent>
                <StyledVideoLength>
                  {props.durationInSeconds
                    ? toHHMMSS(props.durationInSeconds)
                    : "00:00:00"}
                </StyledVideoLength>
              </StyledVideoLengthContainer>
            </StyledSubContainer>
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  );
};

export default Thumbnail;
